/**
 * ConfirmDialog - Reusable confirmation dialog component
 * Replaces native window.confirm() and alert()
 */
import { useState, useCallback, type ReactNode } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import {
  Warning,
  Info,
  CheckCircle,
  Error,
} from '@mui/icons-material';

export type DialogType = 'confirm' | 'info' | 'success' | 'error' | 'warning';

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: string;
  type?: DialogType;
  icon?: ReactNode;
  confirmIcon?: ReactNode;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel: () => void;
  showCancel?: boolean;
}

const iconMap = {
  confirm: <Warning color="warning" sx={{ fontSize: 40, mr: 2 }} />,
  info: <Info color="info" sx={{ fontSize: 40, mr: 2 }} />,
  success: <CheckCircle color="success" sx={{ fontSize: 40, mr: 2 }} />,
  error: <Error color="error" sx={{ fontSize: 40, mr: 2 }} />,
  warning: <Warning color="warning" sx={{ fontSize: 40, mr: 2 }} />,
};

const buttonColorMap: Record<DialogType, 'primary' | 'error' | 'success' | 'warning' | 'info'> = {
  confirm: 'primary',
  info: 'info',
  success: 'success',
  error: 'error',
  warning: 'warning',
};

export default function ConfirmDialog({
  open,
  title,
  message,
  type = 'confirm',
  icon,
  confirmIcon,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  onConfirm,
  onCancel,
  showCancel = true,
}: ConfirmDialogProps) {
  const dialogIcon = icon ?? iconMap[type];

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        {dialogIcon && (
          <Box sx={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}>
            {dialogIcon}
          </Box>
        )}
        <Box component="span">{title}</Box>
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          {message}
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {showCancel && (
          <Button onClick={onCancel} color="inherit">
            {cancelText}
          </Button>
        )}
        <Button
          onClick={onConfirm}
          variant="contained"
          color={buttonColorMap[type]}
          startIcon={confirmIcon}
          autoFocus
        >
          {confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

interface UseConfirmOptions {
  title: string;
  message: string;
  type?: DialogType;
  icon?: ReactNode;
  confirmIcon?: ReactNode;
  confirmText?: string;
  cancelText?: string;
}

export function useConfirmDialog() {
  const [dialogState, setDialogState] = useState<{
    open: boolean;
    title: string;
    message: string;
    type: DialogType;
    icon: ReactNode;
    confirmIcon: ReactNode;
    confirmText: string;
    cancelText: string;
    showCancel: boolean;
    resolve: ((value: boolean) => void) | null;
  }>({
    open: false,
    title: '',
    message: '',
    type: 'confirm',
    icon: null,
    confirmIcon: null,
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    showCancel: true,
    resolve: null,
  });
  const resolveDialog = dialogState.resolve;

  const confirm = useCallback((options: UseConfirmOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      setDialogState({
        open: true,
        title: options.title,
        message: options.message,
        type: options.type || 'confirm',
        icon: options.icon ?? null,
        confirmIcon: options.confirmIcon ?? null,
        confirmText: options.confirmText || 'Confirm',
        cancelText: options.cancelText || 'Cancel',
        showCancel: true,
        resolve,
      });
    });
  }, []);

  const alert = useCallback((options: Omit<UseConfirmOptions, 'cancelText'>): Promise<boolean> => {
    return new Promise((resolve) => {
      setDialogState({
        open: true,
        title: options.title,
        message: options.message,
        type: options.type || 'info',
        icon: options.icon ?? null,
        confirmIcon: options.confirmIcon ?? null,
        confirmText: options.confirmText || 'OK',
        cancelText: '',
        showCancel: false,
        resolve,
      });
    });
  }, []);

  const handleConfirm = useCallback(() => {
    resolveDialog?.(true);
    setDialogState((prev) => ({ ...prev, open: false, resolve: null }));
  }, [resolveDialog]);

  const handleCancel = useCallback(() => {
    resolveDialog?.(false);
    setDialogState((prev) => ({ ...prev, open: false, resolve: null }));
  }, [resolveDialog]);

  const DialogComponent = (
    <ConfirmDialog
      open={dialogState.open}
      title={dialogState.title}
      message={dialogState.message}
      type={dialogState.type}
      icon={dialogState.icon}
      confirmIcon={dialogState.confirmIcon}
      confirmText={dialogState.confirmText}
      cancelText={dialogState.cancelText}
      showCancel={dialogState.showCancel}
      onConfirm={handleConfirm}
      onCancel={handleCancel}
    />
  );

  return { confirm, alert, DialogComponent };
}
